import { RICH_TEXT_HREF_RE, markdownToHtml, markdownToPlainText } from "./richTextMarkdown";

/**
 * Emptiness test for the HTML mirror of a rich-text block.
 *
 * TipTap/ProseMirror never serialise an empty document as an empty string:
 * the empty doc is one empty paragraph, `<p></p>` (or `<p><br></p>` /
 * `<p>&nbsp;</p>` after some paste paths). Any code that decides whether a
 * block "has text" from its stored HTML must treat those the same as `""`,
 * otherwise an editor that has not yet received its content can pass its own
 * blank state off as a real edit.
 */
const EMPTY_RICH_TEXT_HTML_RE =
  /^(?:\s|<p(?:\s[^>]*)?>(?:\s|<br(?:\s[^>]*)?\/?>|&nbsp;|&#160;|&#xa0;)*<\/p>)*$/i;

/** True for `undefined`/`null`, `""`, whitespace, and TipTap's empty-doc HTML. */
export function isEmptyRichTextHtml(html: unknown): boolean {
  if (html === undefined || html === null) return true;
  if (typeof html !== "string") return false;
  return EMPTY_RICH_TEXT_HTML_RE.test(html);
}

/**
 * Rendering formatted text that came from somewhere else (a job's
 * description, written in ISM's editor or imported from an email).
 *
 * ISM stores such text in one of two shapes: HTML it has already run
 * through its own save-time allowlist (`span`s separated by `<br>`,
 * lists, bold/italic/underline, links, images), or plain text with
 * newlines from the older editors and the importers. A report has to
 * show both with their line structure intact, and because the HTML
 * reaches the page through `dangerouslySetInnerHTML` (on the web and in
 * the PDF renderer's server-side render alike) it is rebuilt here from an
 * allowlist of its own rather than trusted: every tag is re-emitted from
 * its parsed name, attributes are dropped except a vetted `href`, and
 * anything that is not an allowed tag is either unwrapped or, for the
 * script-like elements, removed with its contents.
 */

/** Tags re-emitted as themselves. Everything else is unwrapped. */
const RICH_TEXT_ALLOWED_TAGS = new Set([
  "p",
  "div",
  "br",
  "hr",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "sub",
  "sup",
  "ul",
  "ol",
  "li",
  "span",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
]);

/** Void tags: never pushed on the open stack, never closed. */
const RICH_TEXT_VOID_TAGS = new Set(["br", "hr"]);

/**
 * Removed together with everything inside them. The raw-text ones among
 * them are skipped by the tokeniser already; the rest are parsed and
 * dropped tag by tag until their end. `embed` is not here: it is a void
 * element, so it goes like any other unknown tag, with nothing inside.
 * Nor is `head`, whose end tag is usually left out (the body's first tag
 * closes it): it is unwrapped, and its children (title, style, script,
 * meta, link) go one by one.
 */
const RICH_TEXT_DROP_WITH_CONTENT = new Set([
  "script",
  "style",
  "title",
  "template",
  "iframe",
  "object",
  "noscript",
  "svg",
  "math",
  "textarea",
  "select",
]);

/**
 * Elements whose content a browser reads as text up to the matching end
 * tag, never as markup (script data, RAWTEXT and RCDATA states), so a
 * quote or a `<` inside them cannot swallow what follows.
 */
const RICH_TEXT_RAW_TEXT_TAGS = new Set([
  "script",
  "style",
  "textarea",
  "title",
  "iframe",
  "noembed",
  "noframes",
  "xmp",
  "noscript",
  "plaintext",
]);

/** Foreign content, the one place a self-closing flag means something. */
const RICH_TEXT_FOREIGN_TAGS = new Set(["svg", "math"]);

/** Tags whose edges are line edges in the plain reading. */
const RICH_TEXT_BLOCK_TAGS = new Set([
  "p",
  "div",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "tr",
  "blockquote",
]);

/**
 * Whether a stored value is HTML rather than plain text: an opening tag
 * with a known name (every tag the sanitiser keeps or drops, and the
 * common ones it unwraps), followed by a space, a slash or the closing
 * bracket. Plain text that merely mentions an angle bracket
 * (`<create-job-x@in.eaco.me>`, `a <b`) does not match.
 */
const RICH_TEXT_KNOWN_TAGS = [
  ...RICH_TEXT_ALLOWED_TAGS,
  ...RICH_TEXT_DROP_WITH_CONTENT,
  ...RICH_TEXT_RAW_TEXT_TAGS,
  "plaintext",
  "html",
  "body",
  "img",
  "table",
  "thead",
  "tbody",
  "tr",
  "td",
  "th",
  "font",
  "pre",
  "code",
  "center",
  "small",
  "big",
  "meta",
  "link",
  "embed",
  "form",
  "input",
  "button",
  "label",
  "section",
  "article",
  "header",
  "footer",
  "nav",
  "main",
  "aside",
  "figure",
  "dl",
  "dt",
  "dd",
];
const RICH_TEXT_HTML_RE = new RegExp(`<(?:${RICH_TEXT_KNOWN_TAGS.join("|")})(?=[\\s/>])`, "i");

export function looksLikeRichTextHtml(raw: string): boolean {
  return RICH_TEXT_HTML_RE.test(raw);
}

const escapeHtmlText = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Plain text as HTML: escaped, each newline a `<br>`. */
export function plainTextToHtml(text: string): string {
  return escapeHtmlText(text.replace(/\r\n?/g, "\n").trim()).replace(/\n/g, "<br>");
}

type RichTextToken =
  /** `raw`: the body of a raw-text element, markup and entities and all. */
  | { kind: "text"; text: string; raw?: boolean }
  | {
      kind: "tag";
      name: string;
      closing: boolean;
      selfClosing: boolean;
      attrs: Array<[name: string, value: string]>;
    };

const CH_GT = 62;
const CH_SLASH = 47;
const CH_EQ = 61;
const CH_BANG = 33;
const CH_QUESTION = 63;
const CH_DQUOTE = 34;
const CH_SQUOTE = 39;
const CH_LT = 60;
const CH_DASH = 45;

const isAsciiLetter = (c: number): boolean => (c >= 65 && c <= 90) || (c >= 97 && c <= 122);
const isSpace = (c: number): boolean => c === 32 || c === 9 || c === 10 || c === 12 || c === 13;
/** Whether a tag name ends here: a space, `/`, `>` or the end of the input. */
const isTagEnd = (text: string, at: number): boolean =>
  at >= text.length ||
  isSpace(text.charCodeAt(at)) ||
  text.charCodeAt(at) === CH_SLASH ||
  text.charCodeAt(at) === CH_GT;
/** ASCII-only lowercasing: length-preserving, so offsets carry across. */
const asciiLower = (text: string): string => text.replace(/[A-Z]+/g, (run) => run.toLowerCase());

/**
 * Where the body of a raw-text element ends: the index of its end tag's
 * `<`, or the input's length when there is none. For `script`, the
 * escaped states apply: after `<!--` a nested `<script>` makes the next
 * `</script>` part of the body, until `-->`. `plaintext` runs to the end
 * of the input. One pass, no backtracking.
 */
function rawTextEnd(text: string, lower: string, name: string, from: number): number {
  const n = text.length;
  if (name === "plaintext") return n;
  const needle = "</" + name;
  let escaped = false;
  let doubleEscaped = false;
  let q = from;
  while (q < n) {
    const c = text.charCodeAt(q);
    if (c === CH_LT) {
      if (lower.startsWith(needle, q) && isTagEnd(text, q + needle.length)) {
        if (!doubleEscaped) return q;
        doubleEscaped = false;
        q += needle.length;
        continue;
      }
      if (name === "script") {
        if (!escaped && text.startsWith("<!--", q)) {
          // `<!--` followed by dashes and `>` (`<!-->`, `<!--->`) opens
          // nothing: the dash-dash state exits on that `>`.
          q += 4;
          while (q < n && text.charCodeAt(q) === CH_DASH) q += 1;
          if (q < n && text.charCodeAt(q) === CH_GT) q += 1;
          else escaped = true;
          continue;
        }
        if (escaped && !doubleEscaped && lower.startsWith("<script", q) && isTagEnd(text, q + 7)) {
          doubleEscaped = true;
          q += 7;
          continue;
        }
      }
    } else if (c === CH_DASH && escaped) {
      // Two or more dashes then `>` end the escape.
      let r = q;
      while (r < n && text.charCodeAt(r) === CH_DASH) r += 1;
      if (r - q >= 2 && r < n && text.charCodeAt(r) === CH_GT) {
        escaped = false;
        doubleEscaped = false;
        q = r + 1;
        continue;
      }
      q = r;
      continue;
    }
    q += 1;
  }
  return n;
}

/**
 * Where a comment that opened at `from` (just past `<!--`) ends, or the
 * input's length when it never does: `-->` or `--!>`, found in one pass.
 */
function commentEnd(text: string, from: number): number {
  const n = text.length;
  let q = from;
  while (q < n) {
    q = text.indexOf("--", q);
    if (q === -1) return n;
    if (text.charCodeAt(q + 2) === CH_GT) return q + 3;
    if (text.charCodeAt(q + 2) === CH_BANG && text.charCodeAt(q + 3) === CH_GT) return q + 4;
    q += 1;
  }
  return n;
}

/**
 * One left-to-right pass over the markup, the way a browser's tokeniser
 * reads it and in time linear in its length (no backtracking, so a
 * malformed value cannot hold the binder up). Comments are skipped, with
 * the endings a browser accepts (`-->`, `--!>`, and the empty `<!-->`)
 * and an unterminated one to the end of the input as a browser hides it;
 * `<!doctype>`, `<?xml?>` and `<![CDATA[` are skipped to the next `>`;
 * the body of a raw-text element is skipped to its end tag; a tag left
 * open at the end of the input is dropped with what follows, which is
 * also what a browser shows; a `<` that starts no tag is text. Quotes
 * delimit only an attribute value, after `=`.
 */
function tokeniseRichText(text: string): RichTextToken[] {
  const tokens: RichTextToken[] = [];
  const n = text.length;
  let lower: string | null = null;
  let textStart = 0;
  let i = 0;
  // Inside svg or math (foreign content) a `style` or `title` is an
  // ordinary element, not raw text.
  let foreignDepth = 0;
  const flushText = (end: number) => {
    if (end > textStart) tokens.push({ kind: "text", text: text.slice(textStart, end) });
  };

  while (i < n) {
    const lt = text.indexOf("<", i);
    if (lt === -1) break;
    const next = lt + 1 < n ? text.charCodeAt(lt + 1) : -1;

    if (text.startsWith("<!--", lt)) {
      flushText(lt);
      let end: number;
      if (text.startsWith(">", lt + 4)) end = lt + 5;
      else if (text.startsWith("->", lt + 4)) end = lt + 6;
      else end = commentEnd(text, lt + 4);
      i = textStart = end;
      continue;
    }
    if (next === CH_BANG || next === CH_QUESTION) {
      flushText(lt);
      const end = text.indexOf(">", lt + 2);
      i = textStart = end === -1 ? n : end + 1;
      continue;
    }

    // `</` followed by anything but a letter: `</>` is discarded and the
    // rest (`</! hidden>`) is a bogus comment to the next `>`, hidden.
    if (next === CH_SLASH && !(lt + 2 < n && isAsciiLetter(text.charCodeAt(lt + 2)))) {
      flushText(lt);
      if (lt + 2 >= n) {
        textStart = n;
        break;
      }
      if (text.charCodeAt(lt + 2) === CH_GT) {
        i = textStart = lt + 3;
        continue;
      }
      const end = text.indexOf(">", lt + 2);
      i = textStart = end === -1 ? n : end + 1;
      continue;
    }

    let p = lt + 1;
    const closing = next === CH_SLASH;
    if (closing) p += 1;
    // A tag name starts with a letter and runs to a space, `/` or `>`:
    // `a:custom` is its own (unknown) name, not an `a` with attributes.
    const nameStart = p;
    if (p < n && isAsciiLetter(text.charCodeAt(p))) {
      p += 1;
      while (!isTagEnd(text, p)) p += 1;
    }
    if (p === nameStart) {
      i = lt + 1;
      continue;
    }
    const name = asciiLower(text.slice(nameStart, p));

    const attrs: Array<[string, string]> = [];
    let selfClosing = false;
    let ended = false;
    while (p < n) {
      let c = text.charCodeAt(p);
      if (isSpace(c)) {
        p += 1;
        continue;
      }
      if (c === CH_GT) {
        ended = true;
        break;
      }
      if (c === CH_SLASH) {
        selfClosing = p + 1 < n && text.charCodeAt(p + 1) === CH_GT;
        p += 1;
        continue;
      }
      // An attribute name runs to a space, `/`, `>` or `=`; a `=` in
      // first place is part of the name (`=href` is not `href`).
      const attrStart = p;
      if (c === CH_EQ) p += 1;
      while (p < n) {
        c = text.charCodeAt(p);
        if (isSpace(c) || c === CH_SLASH || c === CH_GT || c === CH_EQ) break;
        p += 1;
      }
      const attrName = asciiLower(text.slice(attrStart, p));
      while (p < n && isSpace(text.charCodeAt(p))) p += 1;
      let value = "";
      if (p < n && text.charCodeAt(p) === CH_EQ) {
        p += 1;
        while (p < n && isSpace(text.charCodeAt(p))) p += 1;
        const q = p < n ? text.charCodeAt(p) : -1;
        if (q === CH_DQUOTE || q === CH_SQUOTE) {
          const end = text.indexOf(String.fromCharCode(q), p + 1);
          if (end === -1) {
            p = n;
            break;
          }
          value = text.slice(p + 1, end);
          p = end + 1;
        } else {
          const valueStart = p;
          while (p < n) {
            c = text.charCodeAt(p);
            if (isSpace(c) || c === CH_GT) break;
            p += 1;
          }
          value = text.slice(valueStart, p);
        }
      }
      attrs.push([attrName, value]);
    }

    flushText(lt);
    if (!ended) {
      textStart = n;
      break;
    }
    tokens.push({ kind: "tag", name, closing, selfClosing, attrs });
    i = textStart = p + 1;

    if (RICH_TEXT_FOREIGN_TAGS.has(name)) {
      if (closing) foreignDepth = Math.max(0, foreignDepth - 1);
      else if (!selfClosing) foreignDepth += 1;
      continue;
    }
    if (!closing && foreignDepth === 0 && RICH_TEXT_RAW_TEXT_TAGS.has(name)) {
      // The body, to the end tag or to the end of the input, is one raw
      // text token: whoever keeps the element shows it as text.
      if (lower === null) lower = asciiLower(text);
      const end = rawTextEnd(text, lower, name, i);
      if (end > i) tokens.push({ kind: "text", text: text.slice(i, end), raw: true });
      i = textStart = end;
    }
  }
  flushText(n);
  return tokens;
}

/**
 * Links keep an `href` only when it goes somewhere a report can follow.
 * The stored value is decoded first (an `&amp;` in a query string is one
 * `&`), the characters a browser ignores inside a URL are removed so they
 * cannot hide a scheme, and the result is escaped once when written.
 */
function safeHref(attrs: Array<[string, string]>): string | null {
  const attr = attrs.find(([name]) => name === "href");
  if (!attr) return null;
  const href = decodeAttributeEntities(attr[1])
    .replace(/[\t\n\r]/g, "")
    .trim();
  if (!RICH_TEXT_HREF_RE.test(href)) return null;
  return href;
}

/**
 * HTML rebuilt from the allowlist above: every tag is re-emitted from its
 * parsed name, attributes are dropped except a vetted `href`, unknown tags
 * are unwrapped, the script-like ones removed with their contents,
 * unclosed tags closed and stray closing tags dropped, so the result is a
 * self-contained fragment. Leading and trailing breaks (ISM ends every
 * saved paragraph with one) are trimmed. Idempotent: running it over its
 * own output changes nothing, which is what lets a renderer apply it again
 * at the point the HTML is set on the page.
 */
export function sanitiseRichTextHtml(html: string | null | undefined): string {
  if (html === null || html === undefined) return "";
  const text = String(html).replace(/\r\n?/g, "\n");
  if (text.trim() === "") return "";

  const out: string[] = [];
  const open: string[] = [];
  // How many of each tag are open: a closing tag with nothing to close is
  // skipped without a walk down the stack, so a run of stray closes stays
  // linear; a walk that does happen pops what it passes.
  const openCount = new Map<string, number>();
  const push = (name: string) => {
    open.push(name);
    openCount.set(name, (openCount.get(name) ?? 0) + 1);
  };
  const pop = () => {
    const name = open.pop() as string;
    openCount.set(name, (openCount.get(name) ?? 1) - 1);
    out.push(`</${name}>`);
  };
  // The element being dropped with its contents and how deep the same
  // element is nested inside it, so an inner end tag does not end it.
  let dropping: string | null = null;
  let droppingDepth = 0;

  for (const token of tokeniseRichText(text)) {
    if (token.kind === "text") {
      if (dropping) continue;
      // Text between tags is already HTML text: entities stay as they are;
      // only a bare `<` that did not form a tag is escaped. A raw-text
      // body is text through and through, so all of it is escaped.
      out.push(token.raw ? escapeHtmlText(token.text) : token.text.replace(/</g, "&lt;"));
      continue;
    }
    const { name, closing, selfClosing, attrs } = token;

    if (dropping) {
      if (name === dropping) {
        if (closing) droppingDepth -= 1;
        else if (!(selfClosing && RICH_TEXT_FOREIGN_TAGS.has(name))) droppingDepth += 1;
        if (droppingDepth === 0) dropping = null;
      }
      continue;
    }
    if (RICH_TEXT_DROP_WITH_CONTENT.has(name)) {
      // A self-closing flag is ignored on an HTML element (`<script/>`
      // still opens one), it only closes foreign content.
      if (!closing && !(selfClosing && RICH_TEXT_FOREIGN_TAGS.has(name))) {
        dropping = name;
        droppingDepth = 1;
      }
      continue;
    }
    if (!RICH_TEXT_ALLOWED_TAGS.has(name)) continue;

    if (RICH_TEXT_VOID_TAGS.has(name)) {
      if (!closing) out.push(`<${name}>`);
      continue;
    }
    if (closing) {
      if (!openCount.get(name)) continue;
      const at = open.lastIndexOf(name);
      while (open.length > at) pop();
      continue;
    }
    if (name === "a") {
      const href = safeHref(attrs);
      out.push(
        href
          ? `<a href="${escapeHtmlText(href)}" target="_blank" rel="noopener noreferrer">`
          : "<a>",
      );
    } else {
      out.push(`<${name}>`);
    }
    if (selfClosing) {
      out.push(`</${name}>`);
    } else {
      push(name);
    }
  }
  while (open.length) pop();

  return trimBreaks(out.join(""));
}

/**
 * Leading and trailing whitespace (the set `trim` removes, so the empty
 * check and this agree) and `<br>`s removed, in one pass each way.
 */
function trimBreaks(html: string): string {
  let start = 0;
  let end = html.length;
  for (;;) {
    if (start < end && /\s/.test(html[start])) start += 1;
    else if (html.startsWith("<br>", start)) start += 4;
    else break;
  }
  for (;;) {
    if (end > start && /\s/.test(html[end - 1])) end -= 1;
    else if (end - 4 >= start && html.startsWith("<br>", end - 4)) end -= 4;
    else break;
  }
  return html.slice(start, end);
}

/**
 * HTML for a stored rich-text value, or `""` when it is blank: HTML is
 * sanitised (`sanitiseRichTextHtml`); anything else is read as the
 * Markdown the web app's description editor saves (`markdownToHtml`),
 * which leaves text without markers as escaped lines joined by `<br>`s.
 */
export function renderRichTextHtml(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  const text = String(raw);
  if (text.trim() === "") return "";
  return looksLikeRichTextHtml(text) ? sanitiseRichTextHtml(text) : markdownToHtml(text);
}

/**
 * The HTML 4 entity set (what ISM's editors and `html_entity_decode`
 * agree on) as `name:codepoint` pairs, unpacked on first use.
 */
const NAMED_ENTITY_TABLE = [
  "AElig:198 Aacute:193 Acirc:194 Agrave:192 Alpha:913 Aring:197 Atilde:195 Auml:196",
  "Beta:914 Ccedil:199 Chi:935 Dagger:8225 Delta:916 ETH:208 Eacute:201 Ecirc:202",
  "Egrave:200 Epsilon:917 Eta:919 Euml:203 Gamma:915 Iacute:205 Icirc:206 Igrave:204",
  "Iota:921 Iuml:207 Kappa:922 Lambda:923 Mu:924 Ntilde:209 Nu:925 OElig:338 Oacute:211",
  "Ocirc:212 Ograve:210 Omega:937 Omicron:927 Oslash:216 Otilde:213 Ouml:214 Phi:934 Pi:928",
  "Prime:8243 Psi:936 Rho:929 Scaron:352 Sigma:931 THORN:222 Tau:932 Theta:920 Uacute:218",
  "Ucirc:219 Ugrave:217 Upsilon:933 Uuml:220 Xi:926 Yacute:221 Yuml:376 Zeta:918 aacute:225",
  "acirc:226 acute:180 aelig:230 agrave:224 alefsym:8501 alpha:945 amp:38 and:8743 ang:8736",
  "apos:39 aring:229 asymp:8776 atilde:227 auml:228 bdquo:8222 beta:946 brvbar:166",
  "bull:8226 cap:8745 ccedil:231 cedil:184 cent:162 chi:967 circ:710 clubs:9827 cong:8773",
  "copy:169 crarr:8629 cup:8746 curren:164 dArr:8659 dagger:8224 darr:8595 deg:176",
  "delta:948 diams:9830 divide:247 eacute:233 ecirc:234 egrave:232 empty:8709 emsp:8195",
  "ensp:8194 epsilon:949 equiv:8801 eta:951 eth:240 euml:235 euro:8364 exist:8707 fnof:402",
  "forall:8704 frac12:189 frac14:188 frac34:190 frasl:8260 gamma:947 ge:8805 gt:62",
  "hArr:8660 harr:8596 hearts:9829 hellip:8230 iacute:237 icirc:238 iexcl:161 igrave:236",
  "image:8465 infin:8734 int:8747 iota:953 iquest:191 isin:8712 iuml:239 kappa:954",
  "lArr:8656 lambda:955 lang:9001 laquo:171 larr:8592 lceil:8968 ldquo:8220 le:8804",
  "lfloor:8970 lowast:8727 loz:9674 lrm:8206 lsaquo:8249 lsquo:8216 lt:60 macr:175",
  "mdash:8212 micro:181 middot:183 minus:8722 mu:956 nabla:8711 nbsp:160 ndash:8211 ne:8800",
  "ni:8715 not:172 notin:8713 nsub:8836 ntilde:241 nu:957 oacute:243 ocirc:244 oelig:339",
  "ograve:242 oline:8254 omega:969 omicron:959 oplus:8853 or:8744 ordf:170 ordm:186",
  "oslash:248 otilde:245 otimes:8855 ouml:246 para:182 part:8706 permil:8240 perp:8869",
  "phi:966 pi:960 piv:982 plusmn:177 pound:163 prime:8242 prod:8719 prop:8733 psi:968",
  "quot:34 rArr:8658 radic:8730 rang:9002 raquo:187 rarr:8594 rceil:8969 rdquo:8221",
  "real:8476 reg:174 rfloor:8971 rho:961 rlm:8207 rsaquo:8250 rsquo:8217 sbquo:8218",
  "scaron:353 sdot:8901 sect:167 shy:173 sigma:963 sigmaf:962 sim:8764 spades:9824 sub:8834",
  "sube:8838 sum:8721 sup:8835 sup1:185 sup2:178 sup3:179 supe:8839 szlig:223 tau:964",
  "there4:8756 theta:952 thetasym:977 thinsp:8201 thorn:254 tilde:732 times:215 trade:8482",
  "uArr:8657 uacute:250 uarr:8593 ucirc:251 ugrave:249 uml:168 upsih:978 upsilon:965",
  "uuml:252 weierp:8472 xi:958 yacute:253 yen:165 yuml:255 zeta:950 zwj:8205 zwnj:8204",
  "AMP:38 COPY:169 GT:62 LT:60 QUOT:34 REG:174",
].join(" ");

let namedEntityCodePoints: Map<string, number> | null = null;
function namedEntityCodePoint(name: string): number | undefined {
  if (!namedEntityCodePoints) {
    namedEntityCodePoints = new Map();
    for (const pair of NAMED_ENTITY_TABLE.split(" ")) {
      const colon = pair.indexOf(":");
      namedEntityCodePoints.set(pair.slice(0, colon), Number(pair.slice(colon + 1)));
    }
  }
  return namedEntityCodePoints.get(name);
}

/**
 * The names a browser also reads without their semicolon (the HTML 2 and
 * 3.2 set), by longest match: `&copyright` is `&copy` + `right` in text.
 */
const LEGACY_ENTITY_NAMES = new Set(
  "AElig AMP Aacute Acirc Agrave Aring Atilde Auml COPY Ccedil ETH Eacute Ecirc Egrave Euml GT Iacute Icirc Igrave Iuml LT Ntilde Oacute Ocirc Ograve Oslash Otilde Ouml QUOT REG THORN Uacute Ucirc Ugrave Uuml Yacute aacute acirc acute aelig agrave amp aring atilde auml brvbar ccedil cedil cent copy curren deg divide eacute ecirc egrave eth euml frac12 frac14 frac34 gt iacute icirc iexcl igrave iquest iuml laquo lt macr micro middot nbsp not ntilde oacute ocirc ograve ordf ordm oslash otilde ouml para plusmn pound quot raquo reg sect shy sup1 sup2 sup3 szlig thorn times uacute ucirc ugrave uml uuml yacute yen yuml".split(" "),
);
const LEGACY_ENTITY_MAX_LENGTH = 6;

/** The longest legacy name that starts `run`, or undefined. */
function legacyEntityPrefix(run: string): string | undefined {
  for (let len = Math.min(LEGACY_ENTITY_MAX_LENGTH, run.length); len >= 2; len -= 1) {
    const candidate = run.slice(0, len);
    if (LEGACY_ENTITY_NAMES.has(candidate)) return candidate;
  }
  return undefined;
}

/** What a browser shows for a numeric reference in the C1 range. */
const C1_CODE_POINTS: Record<number, number> = {
  0x80: 0x20ac,
  0x82: 0x201a,
  0x83: 0x0192,
  0x84: 0x201e,
  0x85: 0x2026,
  0x86: 0x2020,
  0x87: 0x2021,
  0x88: 0x02c6,
  0x89: 0x2030,
  0x8a: 0x0160,
  0x8b: 0x2039,
  0x8c: 0x0152,
  0x8e: 0x017d,
  0x91: 0x2018,
  0x92: 0x2019,
  0x93: 0x201c,
  0x94: 0x201d,
  0x95: 0x2022,
  0x96: 0x2013,
  0x97: 0x2014,
  0x98: 0x02dc,
  0x99: 0x2122,
  0x9a: 0x0161,
  0x9b: 0x203a,
  0x9c: 0x0153,
  0x9e: 0x017e,
  0x9f: 0x0178,
};

/** A numeric reference as text; one a browser cannot show is U+FFFD. */
function codePointText(cp: number): string {
  if (!Number.isFinite(cp) || cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) {
    return "�";
  }
  return String.fromCodePoint(C1_CODE_POINTS[cp] ?? cp);
}

/**
 * Entities decoded the way a browser reads them: a numeric reference with
 * or without its semicolon, all of its digits (with the C1 mapping and a
 * replacement character for an impossible code point); a name from the
 * HTML 4 set with its semicolon, case-sensitive; a legacy name without
 * one, except in an attribute when `=` or a letter or digit follows it
 * (so `?q=&copy=1` keeps its parameter). A name outside that set (the
 * HTML 5 additions such as `&sol;`) stays as written; so does anything
 * that is not a reference.
 */
function decodeEntitiesWith(
  text: string,
  named: (name: string) => string | undefined,
  attribute: boolean,
): string {
  if (text.indexOf("&") === -1) return text;
  return text.replace(
    /&(?:#(\d+);?|#[xX]([0-9a-fA-F]+);?|([a-zA-Z][a-zA-Z0-9]{0,31})(;?))/g,
    (
      whole: string,
      dec: string | undefined,
      hex: string | undefined,
      name: string | undefined,
      semicolon: string | undefined,
      offset: number,
    ) => {
      if (dec) return codePointText(Number(dec));
      if (hex) return codePointText(parseInt(hex, 16));
      const run = name ?? "";
      if (semicolon) {
        const known = named(run);
        if (known !== undefined) return known;
      }
      // An unknown name may still start with a legacy one: `&notit;` is
      // `&not` + `it;`.
      const legacy = legacyEntityPrefix(run);
      if (legacy === undefined) return whole;
      const rest = run.slice(legacy.length) + (semicolon ?? "");
      if (attribute) {
        const after = rest !== "" ? rest.charCodeAt(0) : text.charCodeAt(offset + whole.length);
        if (after === CH_EQ || isAsciiLetter(after) || (after >= 48 && after <= 57)) return whole;
      }
      return (named(legacy) ?? "&" + legacy) + rest;
    },
  );
}

const namedEntityText = (name: string): string | undefined => {
  const cp = namedEntityCodePoint(name);
  return cp === undefined ? undefined : codePointText(cp);
};

/** An attribute value (a link's destination) decoded as the browser would. */
function decodeAttributeEntities(value: string): string {
  return decodeEntitiesWith(value, namedEntityText, true);
}

/**
 * Text decoded for the plain reading, where a non-breaking space is a
 * plain one: that reading is for headings, table cells and search rather
 * than layout.
 */
function decodeEntities(text: string): string {
  return decodeEntitiesWith(text, (name) => (name === "nbsp" ? " " : namedEntityText(name)), false);
}

/** Trailing spaces and tabs removed, in one pass. */
function trimTrailingBlanks(chunk: string): string {
  let end = chunk.length;
  while (end > 0 && (chunk.charCodeAt(end - 1) === 32 || chunk.charCodeAt(end - 1) === 9)) end -= 1;
  return end === chunk.length ? chunk : chunk.slice(0, end);
}

/**
 * The plain-text reading of a stored rich-text value: line breaks become
 * newlines, so does the edge of a block (a paragraph, a heading, a list
 * item, whether its end tag was written or not, which is where ISM's
 * `OutputFormatter::htmlToPlainText` reads the role-resolved
 * `description` to its line structure), every tag goes, entities are
 * decoded. A break written out (`<br>`) always counts, so two of them
 * keep a blank line; a block edge only adds a newline where the line is
 * not already broken, so an empty paragraph adds nothing. Anything else
 * is read as the editor's Markdown with its markers removed
 * (`markdownToPlainText`). Read from the same tokens as the HTML, so a
 * `>` inside an attribute or an unterminated comment cannot leak into
 * the text.
 */
export function richTextToPlainText(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "";
  const text = String(raw).replace(/\r\n?/g, "\n");
  if (!looksLikeRichTextHtml(text)) return markdownToPlainText(text);

  const out: string[] = [];
  let dropping: string | null = null;
  let droppingDepth = 0;
  const atLineStart = () => out.length === 0 || out[out.length - 1].endsWith("\n");
  const lineBreak = () => {
    if (out.length > 0) out[out.length - 1] = trimTrailingBlanks(out[out.length - 1]);
    out.push("\n");
  };

  for (const token of tokeniseRichText(text)) {
    if (token.kind === "text") {
      if (dropping) continue;
      let chunk = token.raw ? token.text : decodeEntities(token.text);
      // Whitespace at the start of a line is layout, not text.
      if (atLineStart()) chunk = chunk.replace(/^\s+/, "");
      if (chunk !== "") out.push(chunk);
      continue;
    }
    const { name, closing, selfClosing } = token;
    if (dropping) {
      if (name === dropping) {
        if (closing) droppingDepth -= 1;
        else if (!(selfClosing && RICH_TEXT_FOREIGN_TAGS.has(name))) droppingDepth += 1;
        if (droppingDepth === 0) dropping = null;
      }
      continue;
    }
    if (RICH_TEXT_DROP_WITH_CONTENT.has(name)) {
      if (!closing && !(selfClosing && RICH_TEXT_FOREIGN_TAGS.has(name))) {
        dropping = name;
        droppingDepth = 1;
      }
      continue;
    }
    if (RICH_TEXT_VOID_TAGS.has(name)) {
      lineBreak();
      continue;
    }
    if (RICH_TEXT_BLOCK_TAGS.has(name) && !atLineStart()) lineBreak();
  }
  return out
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
